import type { CreatorActivity, CreatorTriggerType, CreatorUpcomingAutomation, UpcomingPolicyStep } from "@unbox/api";
import { ChevronDown, Clock, Send01, Zap } from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { TRIGGER_ICON } from "@/pages/sequences/automation-builder";
import { splitSequenceSteps, stepLabel, TRIGGER_EVENT_LABELS } from "@/pages/sequences/sequence-steps-preview";
import { cx } from "@/utils/cx";
import { pluralize } from "@/utils/text";
import { CollapsibleActivityItem } from "./collapsible-activity-item";

const DAY_MS = 24 * 60 * 60 * 1000;
const UPCOMING_CARD_CLASS = "border-dashed border-utility-blue-light-300";

const Expandable = ({ open, children }: { open: boolean; children: ReactNode }) => (
  <div
    className={cx(
      "grid transition-[grid-template-rows] duration-200 ease-out",
      open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
    )}
  >
    <div className="min-h-0 overflow-hidden">{children}</div>
  </div>
);

const TimelineNode = ({ icon: Icon }: { icon: typeof Send01 }) => (
  <span className="flex size-10 items-center justify-center rounded-full border border-dashed border-utility-blue-light-300 bg-utility-blue-light-50">
    <Icon className="size-[18px] text-utility-blue-light-500" />
  </span>
);

const TimelineText = ({ children }: { children: ReactNode }) => (
  <div className="flex items-center gap-3 pb-4">
    <TimelineNode icon={Clock} />
    <span className="text-sm text-secondary">{children}</span>
  </div>
);

const stepTitle = (step: UpcomingPolicyStep) =>
  step.type === "send_message"
    ? "Message creator"
    : stepLabel({ type: step.type, messageTemplateId: null, customMessage: null });

const stepAsActivity = (step: UpcomingPolicyStep, timestamp: number): CreatorActivity => ({
  id: `upcoming-${step.step}`,
  type: "message",
  timestamp,
  creatorId: "",
  initiatedBy: "system",
  direction: "sent",
  messageBody: { id: `upcoming-${step.step}`, type: "TEXT", content: step.messagePreview ?? "" },
});

const daysUntilLabel = (dueAt: string | null) => {
  if (!dueAt) return "Next, platform will";
  const days = Math.ceil((new Date(dueAt).getTime() - Date.now()) / DAY_MS);
  return days <= 0 ? "Today platform will" : `in ${pluralize(days, "day", "days")} platform will`;
};

type TimelineEntry = { kind: "step"; step: UpcomingPolicyStep; timestamp: number } | { kind: "text"; text: string };

// Steps beyond the current one have no dueAt yet, so their time is projected from the
// previous step plus their own delay.
const buildTimeline = (steps: UpcomingPolicyStep[]): TimelineEntry[] => {
  const { trunk, branchStep, takenArm, notTakenArm, armLabels } = splitSequenceSteps(steps);
  const ordered = branchStep ? trunk : [...trunk, ...takenArm, ...notTakenArm].sort((a, b) => a.step - b.step);

  const entries: TimelineEntry[] = [];
  let cursor = Date.now();
  ordered.forEach((step, index) => {
    if (index > 0 && step.delayDays > 0) {
      entries.push({ kind: "text", text: `Wait ${pluralize(step.delayDays, "day", "days")}` });
    }
    cursor = step.dueAt ? new Date(step.dueAt).getTime() : cursor + step.delayDays * DAY_MS;
    entries.push({ kind: "step", step, timestamp: cursor });
  });

  if (branchStep) {
    const nextOf = (arm: UpcomingPolicyStep[]) => (arm[0] ? stepTitle(arm[0]) : "No further action");
    entries.push({ kind: "text", text: `${armLabels?.taken ?? "If true"} → ${nextOf(takenArm)}` });
    entries.push({ kind: "text", text: `${armLabels?.notTaken ?? "If false"} → ${nextOf(notTakenArm)}` });
  }
  return entries;
};

const TimelineRow = ({ entry, showConnectorLine }: { entry: TimelineEntry; showConnectorLine: boolean }) =>
  entry.kind === "text" ? (
    <TimelineText>{entry.text}</TimelineText>
  ) : (
    <CollapsibleActivityItem
      activity={stepAsActivity(entry.step, entry.timestamp)}
      leadingStep={entry.step}
      cardClassName={UPCOMING_CARD_CLASS}
      showConnectorLine={showConnectorLine}
      fullWidth
    />
  );

const UpcomingAutomationCard = ({ automation }: { automation: CreatorUpcomingAutomation }) => {
  const [showFullSequence, setShowFullSequence] = useState(false);
  const timeline = buildTimeline(automation.steps);
  const [first, ...rest] = timeline;
  const TriggerIcon = TRIGGER_ICON[automation.triggerType as CreatorTriggerType] ?? Zap;
  const whenLabel = daysUntilLabel(automation.steps[0]?.dueAt ?? null);

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-utility-blue-light-100 p-4">
      <div className="flex items-center gap-2.5">
        <TriggerIcon className="size-5 shrink-0 text-primary" />
        <span className="flex-1 text-sm font-semibold text-primary">
          {TRIGGER_EVENT_LABELS[automation.triggerType] ?? automation.sequenceName}
        </span>
        {!showFullSequence && <span className="truncate text-sm font-semibold text-brand-primary">{whenLabel}</span>}
      </div>

      <div className="flex flex-col">
        <Expandable open={showFullSequence}>
          <TimelineText>{whenLabel}</TimelineText>
        </Expandable>
        {first && <TimelineRow entry={first} showConnectorLine={showFullSequence && rest.length > 0} />}
        <Expandable open={showFullSequence}>
          {rest.map((entry, index) => (
            <TimelineRow
              key={entry.kind === "step" ? `step-${entry.step.step}` : `text-${entry.text}`}
              entry={entry}
              showConnectorLine={index < rest.length - 1}
            />
          ))}
        </Expandable>
      </div>

      {rest.length > 0 && (
        <button
          type="button"
          onClick={() => setShowFullSequence((open) => !open)}
          className="self-end text-sm text-brand-primary underline decoration-dotted"
        >
          {showFullSequence ? "Hide full sequence" : "See full sequence"}
        </button>
      )}
    </div>
  );
};

export const UpcomingAutomations = ({ automations }: { automations: CreatorUpcomingAutomation[] }) => {
  const [open, setOpen] = useState(false);
  if (automations.length === 0) return null;

  return (
    <div className="w-full max-w-[756px] rounded-md border border-dashed border-utility-blue-light-300 bg-utility-blue-light-50 px-4 py-3">
      <button type="button" onClick={() => setOpen((value) => !value)} className="flex w-full items-center gap-2 py-2">
        <Send01 className="size-[18px] text-utility-blue-light-500" />
        <span className="flex-1 text-left text-sm font-medium text-secondary">
          {pluralize(automations.length, "Upcoming Automation", "Upcoming Automations")} for this Creator
        </span>
        <ChevronDown className={cx("size-6 text-quaternary transition-transform", open && "rotate-180")} />
      </button>
      <Expandable open={open}>
        <div className="flex flex-col gap-3 pt-3">
          {automations.map((automation) => (
            <UpcomingAutomationCard key={automation.runId} automation={automation} />
          ))}
        </div>
      </Expandable>
    </div>
  );
};
